import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import { createContext, FC, ReactNode, useContext, useMemo } from 'react'

import { useValueLookups, ValueLookupValueType } from './api/conditionalFormApi'

interface FieldValueLookupContextType {
  fieldToValues: ReadonlyMap<string, ReadonlyArray<ValueLookupValueType>>
  isLoading: boolean
}

const FieldValueLookupContext = createContext<FieldValueLookupContextType>({
  fieldToValues: new Map(),
  isLoading: false,
})

/**
 * A context to hold the conditional form value lookups for the ticket currently in scope.
 */
function useCreateContext(
  config: Parameters<typeof useValueLookups>[0] | undefined
): FieldValueLookupContextType {
  const { values, isLoading } = useValueLookups(config)

  const fieldToValues = useMemo(() => {
    return new Map(
      Object.entries(groupBy(values ?? [], 'field')).map(([field, vals]) => [
        field,
        sortBy(vals, 'name'),
      ])
    ) as ReadonlyMap<string, ReadonlyArray<ValueLookupValueType>>
  }, [values])

  return {
    fieldToValues,
    isLoading,
  }
}

export function useFieldValueLookup(field: string): {
  values: ReadonlyArray<ValueLookupValueType>
  isLoading: boolean
} {
  const { fieldToValues, isLoading } = useContext(FieldValueLookupContext)
  return useMemo(
    () => ({
      values: fieldToValues.get(field) ?? [],
      isLoading,
    }),
    [fieldToValues, isLoading, field]
  )
}

export const FieldValueLookupProvider: FC<{
  children: ReactNode
  config: Parameters<typeof useValueLookups>[0] | undefined
}> = ({ children, config }) => {
  const context = useCreateContext(config)
  return (
    <FieldValueLookupContext.Provider value={context}>
      {children}
    </FieldValueLookupContext.Provider>
  )
}
